import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, InputNumber, Modal, Row, Select, Space, Spin, message } from "antd";
import { ArrowRightOutlined, ReloadOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

export default function ChangeUnitModal({ application, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const propertySequence = useRef(0);
  const unitSequence = useRef(0);
  const quoteSequence = useRef(0);
  const propId = Form.useWatch("propId", form);
  const unitId = Form.useWatch("unitId", form);

  const searchProperties = useCallback(async (content = "") => {
    const sequence = ++propertySequence.current;
    setPropertyLoading(true);
    try {
      const result = await http.get(api.getPropSearch, { params: { content } });
      if (sequence !== propertySequence.current) return;
      if (result.code !== "1") throw new Error();
      setProperties((result.data || []).map((item) => ({ value: item.propId,
        label: item.propName || item.fullDisplay || item.prop })));
    } catch (_error) {
      if (sequence === propertySequence.current) message.error("Could not load properties");
    } finally {
      if (sequence === propertySequence.current) setPropertyLoading(false);
    }
  }, []);

  const searchUnits = useCallback(async (propertyId, content = "") => {
    const sequence = ++unitSequence.current;
    setUnits([]);
    setUnitLoading(Boolean(propertyId));
    if (!propertyId) return;
    try {
      const result = await http.get(api.unitSearch, { params: { propId: propertyId, content } });
      if (sequence !== unitSequence.current) return;
      if (result.code !== "1") throw new Error();
      setUnits((result.data || []).map((item) => ({ value: item.unitId,
        label: item.unitName || item.unit, disabled: item.unitId === application.unitId })));
    } catch (_error) {
      if (sequence === unitSequence.current) message.error("Could not load units");
    } finally {
      if (sequence === unitSequence.current) setUnitLoading(false);
    }
  }, [application.unitId]);

  const loadQuote = async (id) => {
    const sequence = ++quoteSequence.current;
    setQuote(null);
    form.setFieldsValue({ rent: undefined, deposit: undefined });
    setQuoteLoading(Boolean(id));
    if (!id) return;
    try {
      const result = await http.get(api.creditCheckUnitQuote, { params: { unitId: id } });
      if (sequence !== quoteSequence.current) return;
      if (result.code !== "1" || !result.data || result.data.propId !== form.getFieldValue("propId")) throw new Error();
      setQuote({ ...result.data, unitId: id });
      form.setFieldsValue({ rent: result.data.rentRate, deposit: result.data.deposit });
    } catch (_error) {
      if (sequence === quoteSequence.current) message.error("Could not load unit amounts. Please refresh amounts.");
    } finally {
      if (sequence === quoteSequence.current) setQuoteLoading(false);
    }
  };

  useEffect(() => {
    searchProperties();
    searchUnits(application.propId);
    return () => {
      propertySequence.current += 1;
      unitSequence.current += 1;
      quoteSequence.current += 1;
    };
  }, [application.propId, searchProperties, searchUnits]);

  const submit = async () => {
    if (inFlight.current || quoteLoading || !quote || quote.unitId !== form.getFieldValue("unitId")) return;
    inFlight.current = true;
    setSaving(true);
    try {
      const values = await form.validateFields();
      const result = await http.post(api.creditCheckChangeUnit, {
        applicationId: application.applicationId, sourceUnitId: application.unitId,
        unitId: values.unitId, rent: values.rent, deposit: values.deposit,
        quotedRent: quote.rentRate, quotedDeposit: quote.deposit,
      });
      if (result.code !== "1") {
        message.warning(result.msg || "Could not change unit. Refresh the application and try again.");
        return;
      }
      message.success("Application unit updated");
      onSaved();
    } catch (error) {
      if (!error.errorFields) message.error("Could not change unit. Refresh the application before retrying.");
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return <Modal open centered width={640} title="Change unit" className="cc-detail-modal cc-change-unit-modal"
    maskClosable={false} closable={!saving} keyboard={!saving} onCancel={onClose}
    footer={<Space><Button disabled={saving} onClick={onClose}>Cancel</Button>
      <Button type="primary" loading={saving} disabled={!quote || quoteLoading} onClick={submit}>Save changes</Button></Space>}>
    <div className="cc-transfer-source">
      <div><span>Application #{application.applicationId} · Current unit</span><strong>{application.prop} / {application.unit}</strong></div>
      <ArrowRightOutlined />
      <div><span>New unit</span><strong>{quote ? `${quote.prop} / ${quote.unit}` : "Select below"}</strong></div>
    </div>
    <Form form={form} layout="vertical" disabled={saving} initialValues={{ propId: application.propId }}>
      <Row gutter={16}>
        <Col xs={24} sm={12}><Form.Item name="propId" label="Property" rules={[{ required: true, message: "Select a property" }]}>
          <Select showSearch filterOption={false} options={properties} loading={propertyLoading}
            notFoundContent={propertyLoading ? <Spin size="small" /> : undefined}
            onSearch={searchProperties} onChange={(id) => {
              form.setFieldValue("unitId", undefined);
              loadQuote(null);
              searchUnits(id);
            }} placeholder="Choose property" />
        </Form.Item></Col>
        <Col xs={24} sm={12}><Form.Item name="unitId" label="Unit" rules={[{ required: true, message: "Select a unit" }]}>
          <Select showSearch filterOption={false} options={units} loading={unitLoading} disabled={!propId || saving}
            notFoundContent={unitLoading ? <Spin size="small" /> : undefined}
            onSearch={(content) => searchUnits(propId, content)} onChange={loadQuote} placeholder="Choose unit" />
        </Form.Item></Col>
      </Row>
      <Spin spinning={quoteLoading}>
        <Row gutter={16}>
          <Col span={12}><Form.Item name="rent" label="New base rent" rules={[{ required: true, message: "Enter rent" }]}>
            <InputNumber prefix="$" min={0} max={65000} precision={2} disabled={!quote || saving} style={{ width: "100%" }} />
          </Form.Item></Col>
          <Col span={12}><Form.Item name="deposit" label="Deposit" rules={[{ required: true, message: "Enter deposit" }]}>
            <InputNumber prefix="$" min={0} max={65000} precision={2} disabled={!quote || saving} style={{ width: "100%" }} />
          </Form.Item></Col>
        </Row>
      </Spin>
      <Button type="link" icon={<ReloadOutlined />} disabled={!unitId || saving} loading={quoteLoading}
        onClick={() => loadQuote(unitId)}>Refresh amounts</Button>
    </Form>
    <Alert type="info" showIcon message="Saving also updates the selected unit’s base rent."
      description="The deposit applies to this application. Any additional deposit already on the application is retained." />
  </Modal>;
}
